import { Navigate, type RouteObject } from "react-router-dom";

import { LoginPage } from "@/features/auth/login-page";
import { LandingRedirect, RedirectIfAuthed, RequireAuth, RequireSuperAdmin } from "@/features/auth/guards";
import { PortalLayout } from "@/features/portal/portal-layout";
import { BrandRoute } from "@/features/portal/brand-route";
import { CreateBrandPage } from "@/features/portal/create-brand-page";
import { ThemingPage } from "@/features/theming/theming-page";
import { TerminologyPage } from "@/features/terminology/terminology-page";
import { GamesPage } from "@/features/games/games-page";
import { MembersPage } from "@/features/members/members-page";
import { DemoPage } from "@/features/demo/demo-page";
import { PlacesPage } from "@/features/places/places-page";
import { PlacePage } from "@/features/places/place-page";
import { AllBrandsPage } from "@/features/admin/all-brands-page";
import { PresetsPage } from "@/features/presets/presets-page";
import { ScenePage } from "@/features/scene/scene-page";
import { SCENE_ENABLED } from "@/config";

/** The portal's route table (`frontend.tsx` makes the browser router from it;
 *  tests read it without a browser). */
export const routes: RouteObject[] = [
  // Dev-only living-scene harness (Surface Protocol first impl). Behind the
  // SCENE_ENABLED flag and OUTSIDE the authed portal shell so it can be driven
  // headlessly; makes NO nav/menu IA change. Absent when the flag is off, so the
  // "*" catch-all below redirects `/dev/scene` back to the portal in production.
  ...(SCENE_ENABLED ? [{ path: "/dev/scene", element: <ScenePage /> }] : []),
  {
    path: "/login",
    element: (
      <RedirectIfAuthed>
        <LoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/",
    element: <RequireAuth />,
    children: [
      {
        element: <PortalLayout />,
        children: [
          // Pick a landing destination (first brand / create / admin directory).
          { index: true, element: <LandingRedirect /> },
          { path: "create", element: <CreateBrandPage /> },
          // Brand editors. The brand lives in the path and the selected theme in
          // `?theme`; BrandRoute resolves them, loads the draft, and guards
          // unsaved changes. Replaces the old RequireBrand guard.
          {
            path: "b/:brandId",
            element: <BrandRoute />,
            children: [
              { index: true, element: <Navigate to="theming" replace /> },
              { path: "theming", element: <ThemingPage /> },
              { path: "terminology", element: <TerminologyPage /> },
              { path: "games", element: <GamesPage /> },
              { path: "members", element: <MembersPage /> },
              { path: "places", element: <PlacesPage /> },
              { path: "p/:placeId", element: <PlacePage /> },
              { path: "demo", element: <DemoPage /> },
            ],
          },
          // Admin-only pages: the "All brands" directory and the rules presets.
          // Outside BrandRoute: a super admin with no brands of their own still
          // needs to reach them.
          {
            element: <RequireSuperAdmin />,
            children: [
              { path: "admin/brands", element: <AllBrandsPage /> },
              { path: "admin/presets", element: <PresetsPage /> },
            ],
          },
        ],
      },
    ],
  },
  // Anything else (including legacy section-only URLs like /theming) falls back
  // to the landing redirect via "/".
  { path: "*", element: <Navigate to="/" replace /> },
];
